import {
  isRouteErrorResponse,
  Links,
  Outlet,
  Scripts,
  ScrollRestoration,
} from "react-router"
import { QueryClientProvider } from "@tanstack/react-query"
import * as Sentry from "@sentry/react-router"
import { GoogleOAuthProvider } from "@react-oauth/google"

import type { Route } from "./+types/root"
import { queryClient } from "./store/queryClient"
import "./app.css"
import { useEffect } from "react"
import { ensureFullstoryInitialized } from "~/lib/fullStory.client"
import { initMixPanel } from "~/lib/mixpanel.client"
import { hubspotTrackingSrc } from "~/lib/hubspot"
import { FACEBOOK_PIXEL_SNIPPET } from "~/lib/facebookPixel"
import { mazeSnippet } from "~/lib/maze"
import { useIdentitySync } from "~/store/auth/hooks"
import { useCaptureAdEvent } from "~/lib/adCapture"
import { Toaster } from "~/components/ui/sonner"

export const links: Route.LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
  },
]

export function Layout({ children }: { children: React.ReactNode }) {
  const googleClientId: string = import.meta.env.VITE_GOOGLE_CLIENT_ID as string
  const hubspotSrc = hubspotTrackingSrc()
  const maze = mazeSnippet()

  return (
    <html lang="en" style={{ fontSize: "16px" }}>
      <head>
        <meta charSet="utf-8" />
        {/* Maze asks for its snippet early in <head>; after charset, which
            must land in the first 1024 bytes. */}
        {maze && (
          <script
            id="maze-universal-snippet"
            dangerouslySetInnerHTML={{ __html: maze }}
          />
        )}
        <title>Loan Dashboard</title>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" type="image/png" href="/favicon.png" />
        {/* Before React hydrates: fbevents.js must read `fbclid` off the
            landing URL before the auth gate redirects and drops the query
            string. See FACEBOOK_PIXEL_SNIPPET. */}
        <script
          id="facebook-pixel-script"
          dangerouslySetInnerHTML={{ __html: FACEBOOK_PIXEL_SNIPPET }}
        />
        {hubspotSrc && (
          <script async defer id="hs-script-loader" src={hubspotSrc} />
        )}
        <Links />
      </head>
      <body>
        <GoogleOAuthProvider clientId={googleClientId || ""}>
          <QueryClientProvider client={queryClient}>
            {children}
            <Toaster />
          </QueryClientProvider>
        </GoogleOAuthProvider>
        <Toaster />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  )
}

export default function App() {
  useEffect(() => {
    ensureFullstoryInitialized()
    initMixPanel()
  }, [])

  // Primary identity path: keeps FullStory/Sentry identity in sync with the
  // current user on every load, including plain reloads served from the
  // persisted cache. See useIdentitySync for why this makes identity foolproof.
  useIdentitySync()

  // Fire "User Captured By Ad" (Mixpanel + FullStory) when an ad-referred visit
  // is detected on the entry URL. See adCapture.ts.
  useCaptureAdEvent()

  return <Outlet />
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = "Oops!"
  let details = "An unexpected error occurred."
  let stack: string | undefined

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "404" : "Error"
    details =
      error.status === 404
        ? "The requested page could not be found."
        : error.statusText || details
  } else if (error && error instanceof Error) {
    Sentry.captureException(error)
    if (import.meta.env.DEV) {
      details = error.message
      stack = error.stack
    }
  }

  return (
    <main className="pt-16 p-4 container mx-auto">
      <h1>{message}</h1>
      <p>{details}</p>
      {stack && (
        <pre className="w-full p-4 overflow-x-auto">
          <code>{stack}</code>
        </pre>
      )}
    </main>
  )
}
